<template>
  <div class="qa-client-widget">
    <div class="qa-client-widget__bar">
      <span class="qa-client-widget__label">
        Клиентский виджет{{ brandingName ? `: ${brandingName}` : '' }}
      </span>
      <a-button
        v-if="widgetUrl"
        size="small"
        :href="widgetUrl"
        target="_blank"
        rel="noopener noreferrer">
        Открыть в новой вкладке
      </a-button>
    </div>

    <iframe
      v-if="widgetUrl"
      :key="widgetUrl"
      ref="frame"
      title="client-widget"
      :src="widgetUrl"
      width="100%"
      height="100%"
      allowfullscreen
      allow="fullscreen"
      style="border: none;" />
    <a-empty
      v-else
      :description="emptyText"
      class="qa-client-widget__empty" />
  </div>
</template>

<script setup>
import {
  computed, ref, watch, onMounted, onBeforeUnmount,
} from 'vue';
import { api } from '@/api';
import { getWidgetRoute } from '@/router/helpers';
import { toggleQaFullscreen } from './qaFullscreen';

const props = defineProps({
  crmPlanId: { type: [Number, String], required: true },
  // Эффективный брендинг: планировка → апартамент → ручной выбор в панели.
  brandingId: { type: [Number, String], default: null },
  // Текущий этап проверки — чтобы открыть виджет на той же вкладке, а не всегда на 360.
  stage: { type: String, default: null },
});

const TAB_BY_STAGE = { '2d': 'plan', '3d': 'rotation', tour: 'panorama' };

const frame = ref(null);

const branding = ref(null);
const loading = ref(false);

// Данные брендинга берём публичным эндпоинтом по id — оттуда домен для перехода.
const loadBranding = async (id) => {
  if (!id) {
    branding.value = null;
    return;
  }
  loading.value = true;
  try {
    const { data } = await api.getWidgetBrandingById(id);
    branding.value = data;
  } catch (e) {
    branding.value = null;
  } finally {
    loading.value = false;
  }
};

watch(() => props.brandingId, (id) => loadBranding(id), { immediate: true });

const brandingName = computed(() => branding.value?.name || branding.value?.company_name || '');
const fullDomain = computed(() => {
  const b = branding.value;
  if (!b?.domain) return null;
  return b.domain_prefix ? `${b.domain_prefix}.${b.domain}` : b.domain;
});

const emptyText = computed(() => (loading.value
  ? 'Загрузка клиентского виджета…'
  : 'У выбранного брендинга нет домена для клиентского виджета'));

// Брендированный виджет живёт на своём домене (v4 — по пути /beta/);
// параметры минимальные: планировка, окружение и текущая вкладка.
const widgetUrl = computed(() => {
  if (!fullDomain.value) return '';

  const url = getWidgetRoute({
    crmPlanId: props.crmPlanId,
    qaFullscreenParent: window.location.origin,
    primarytab: TAB_BY_STAGE[props.stage] ?? 'plan',
  }).toString();

  return url.replace(/^https?:\/\/[^/]+\/widget/, `https://${fullDomain.value}/beta/`);
});
const onMessage = (event) => {
  if (!widgetUrl.value || event.source !== frame.value?.contentWindow
    || event.origin !== new URL(widgetUrl.value).origin || event.data?.type !== 'QA_FULLSCREEN') return;
  toggleQaFullscreen().catch(() => {});
};
onMounted(() => window.addEventListener('message', onMessage));
onBeforeUnmount(() => window.removeEventListener('message', onMessage));
</script>

<style scoped>
.qa-client-widget {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 480px;
}
.qa-client-widget__bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
}
.qa-client-widget__label {
  font-weight: 500;
}
.qa-client-widget iframe {
  flex: 1;
  min-height: 0;
}
.qa-client-widget__empty {
  margin: auto;
}
</style>
